<template>
  <v-container class="max-w-4xl mx-auto p-6">
    <h1 class="text-3xl font-bold mb-6">User Guide and Workflow</h1>
    <p class="text-gray-700 mb-4">
      The Micro SRP component is a configurable solution designed for websites
      that display vehicle listings. Each website leveraging this component will
      have a dedicated Admin Dashboard for management and customization. Below
      is an organized guide explaining its functionality and how to use it
      effectively:
    </p>

    <v-expansion-panels>
      <!-- Admin Overview -->
      <v-expansion-panel>
        <v-expansion-panel-title class="font-weight-bold"
          >Admin Overview</v-expansion-panel-title
        >
        <v-expansion-panel-text>
          <section class="my-4">
            <p class="text-gray-700 mb-4">
              The Admin Dashboard is the central hub for configuring and
              managing the MicroSRP component. It offers the following key
              capabilities:
            </p>
            <ul class="my-2 mx-8">
              <li>
                <strong>Placements</strong>
                <ul class="mx-8">
                  <li>Placement Analytics and Performance Dashboard</li>
                  <li>Placement Management</li>
                </ul>
              </li>
              <li>
                <strong>Inventory</strong>
                <ul class="mx-8">
                  <li>Analytics and Performance Dashboard</li>
                </ul>
              </li>
              <li>
                <strong>Trends</strong>
                <ul class="mx-8">
                  <li>Metrics Dashboard</li>
                </ul>
              </li>
              <li>
                <strong>Help</strong>
              </li>
            </ul>
          </section>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Placement Management -->
      <v-expansion-panel>
        <v-expansion-panel-title class="font-weight-bold"
          >Placement Management</v-expansion-panel-title
        >
        <v-expansion-panel-text>
          <section class="my-4">
            <p class="text-gray-700 mb-4">
              The dashboard allows you to create and manage placements for
              different pages on the website.
            </p>
            <ul class="my-2 mx-8">
              <li>
                <strong>Adding Placements:</strong>
                <ul class="mx-8">
                  <li>
                    Create a new placement (aka: listing or ad unit) for display
                    on or off your website.
                    <ul class="mx-8">
                      <li>
                        Be sure to name it based upon where you will be
                        displaying it and any specifics about the inventory
                        filtering/sorting you are targeting with the placement
                      </li>
                    </ul>
                  </li>
                  <li>
                    Define specific configurations for each placement based on
                    the goals.
                  </li>
                </ul>
              </li>
              <li>
                <strong>Listings Display:</strong>
                <ul class="mx-8">
                  <li>
                    Each placement will showcase vehicle listings in a slider
                    format (similar to Google Vehicle Ads)
                  </li>
                  <li>
                    Clicking on a listing card records an interaction,
                    contributing to overall analytics.
                  </li>
                </ul>
              </li>
              <li>
                <strong>Placement Overview:</strong>
                <ul class="mx-8">
                  <li>
                    After customizing and saving a placement to your
                    satisfaction, it will appear in the
                    <strong>Placements Tab.</strong>
                  </li>
                  <li>
                    Each placement will have an option to "Copy Embed Code,"
                    which generates a script. Use the script directly into your
                    website to deploy the placement. Once embedded, the
                    placement will be live and functional.
                  </li>
                </ul>
              </li>
              <li>
                <strong>Placement Customization:</strong>
                <ul class="mx-8">
                  <li>
                    <strong>Filters:</strong> select the inventory you want by
                    model, condition, days on lot and more
                  </li>
                  <li><strong>Count:</strong> select max number to display</li>
                  <li>
                    <strong>Sort:</strong> select sort order. Powerful for
                    showing the newest, oldest, lowest price or highest price in
                    inventory. Use with max count of 5 and you will always show
                    the lowest (or highest) priced in inventory. Great for New
                    Arrivals (sort by ascending days on lot) or Great Deals
                    (sort by oldest days on lot).
                  </li>
                  <li>
                    <strong> Look & Feel:</strong> type size, colors, font, CTAs
                    <ul>
                      <li>
                        Configure the display of vehicle listings with options
                        for layout, filters, and display settings.
                      </li>
                    </ul>
                  </li>
                </ul>
              </li>
            </ul>
          </section>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Analytics and Performance Tracking -->
      <v-expansion-panel>
        <v-expansion-panel-title class="font-weight-bold"
          >Analytics and Performance Tracking</v-expansion-panel-title
        >
        <v-expansion-panel-text>
          <section class="my-4">
            <p class="text-gray-700 mb-4">
              The analytics and performance dashboards provide usage statistics
              to monitor the performance of the MicroSRP component. Key metrics
              include:
            </p>
            <ul class="my-2 mx-8">
              <li>
                Track the total number of placements added for the website.
              </li>
              <li>
                <strong>Click Statistics:</strong>
                <ul class="mx-8">
                  <li>Tracks the total number of clicks for each placement.</li>
                  <li>Displays the Clickthrough Rate (CTR) as a percentage.</li>
                </ul>
              </li>
              <li>
                <strong>Load Metrics:</strong>
                <ul class="mx-8">
                  <li>Measures the number of loads for each placement.</li>
                  <li>Displays load percentages for performance insights.</li>
                </ul>
              </li>
              <li>
                <strong>Filters and Comparisons:</strong>
                <ul class="mx-8">
                  <li>
                    Apply date filters to view performance over specific time
                    periods.
                  </li>
                  <li>
                    Compare data across different time frames for trends and
                    insights.
                  </li>
                </ul>
              </li>
              <li>
                <strong>Additional Metrics:</strong>
                <ul class="mx-8">
                  <li>Total number of vehicles listed.</li>
                  <li>Impression counts.</li>
                  <li>A trends graph for visualizing performance over time.</li>
                </ul>
              </li>
            </ul>
          </section>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Workflow for Customizing and Integrating Micro SRP -->
      <v-expansion-panel>
        <v-expansion-panel-title class="font-weight-bold"
          >Workflow for Customizing and Integrating Micro
          SRP</v-expansion-panel-title
        >
        <v-expansion-panel-text>
          <section class="my-4">
            <ol class="my-2 mx-8">
              <li>
                <strong>Access the Admin Dashboard:</strong>
                <ul class="mx-8">
                  <li>
                    Log in to the Admin Dashboard associated with your
                    website(each website has a specific account_id).
                  </li>
                </ul>
              </li>
              <li>
                <strong>Create or Edit a Placement:</strong>
                <ul class="mx-8">
                  <li>Navigate to the placements section.</li>
                  <li>
                    Add a new placement or edit an existing one with specific
                    customizations (e.g., layout, filters).
                  </li>
                </ul>
              </li>
              <li>
                <strong>Preview the Component:</strong>
                <ul class="mx-8">
                  <li>
                    Use the real-time preview feature to verify how the
                    placement will appear on the website with the selected
                    filters and customizations
                  </li>
                </ul>
              </li>
              <li>
                <strong>Save and Deploy the Placement:</strong>
                <ul class="mx-8">
                  <li>
                    Save your customizations and make the placement perfect for
                    your needs.
                  </li>
                  <li>
                    Go to the <strong>Placements Tab</strong> to see all created
                    placements.
                  </li>
                  <li>
                    Click on a placement to access the
                    <strong>"Copy Embed Code"</strong> option.
                  </li>
                  <li>
                    Generate the script and insert it directly into your website
                    to make the placement live.
                  </li>
                </ul>
              </li>
              <li>
                <strong>Monitor Performance:</strong>
                <ul class="mx-8">
                  <li>
                    Regularly check the dashboard for updated performance
                    analytics.
                  </li>
                  <li>
                    Use the data to refine configurations and improve user
                    engagement.
                  </li>
                </ul>
              </li>
            </ol>
          </section>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <section class="mt-8">
        For help, contact
        <a href="mailto:support@jazel.com">support@jazel.com</a> or visit
        <a href="https://www.jazel.com">https://www.jazel.com</a> to chat with
        us.
      </section>
    </v-expansion-panels>
  </v-container>
</template>

<script setup lang="ts"></script>
